// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import { useStepsData } from '@shared/ui-kit';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const usePendingChecklist = () => {
  const {
    proposal,
    markStepAsCompleted,
    skipAccountOpeningPhase,
    resumeToProfilingResults,
    setNavigationFloor,
    hasExistingInversisAccount,
    hasCompletedProfiling,
    hasPartialProfilingData,
    canResumeToProfilingResults,
    isAwaitingContractSignature,
  } = useAccountWorkflowData();
  const { next, navigate } = useStepsData();
  const { t } = useTranslation();

  const advisoryTypeName = useMemo(() => {
    const advisoryType = proposal?.advisory_type || 'non-independent';

    return t(`activation-step2.advisory-types.${advisoryType}.name`, {
      ns: 'components',
    }) as string;
  }, [proposal?.advisory_type, t]);

  const onStart = useCallback(() => {
    markStepAsCompleted('pending-checklist');

    if (isAwaitingContractSignature && hasExistingInversisAccount) {
      resumeToProfilingResults();
      setNavigationFloor('confirmation');
      navigate('confirmation');

      return;
    }

    if (canResumeToProfilingResults && hasExistingInversisAccount) {
      resumeToProfilingResults();
      setNavigationFloor('result-and-signature');
      navigate('result-and-signature');

      return;
    }

    if (hasExistingInversisAccount) {
      skipAccountOpeningPhase();
      setNavigationFloor('contract-review');
      navigate('contract-review');

      return;
    }

    setNavigationFloor('personal-data');
    next();
  }, [
    canResumeToProfilingResults,
    navigate,
    hasExistingInversisAccount,
    isAwaitingContractSignature,
    markStepAsCompleted,
    next,
    resumeToProfilingResults,
    setNavigationFloor,
    skipAccountOpeningPhase,
  ]);

  return {
    advisoryTypeName,
    hasExistingInversisAccount,
    hasCompletedProfiling,
    hasPartialProfilingData,
    canResumeToProfilingResults,
    isAwaitingContractSignature,
    onStart,
  };
};
